import React, { useState, useEffect } from 'react';
import { Outlet, useMatch, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { usePermissions } from '../context/PermissionContext';
import { useLanguage } from '../context/LanguageContext';
import { useOrderNotifications } from '../context/OrderNotificationContext';
import { useMessaging } from '../context/MessagingContext';
import useAccessibleNavItems from '../hooks/useAccessibleNavItems';
import useActiveProductionRun from '../hooks/useActiveProductionRun';
import useQaReviewQueue from '../hooks/useQaReviewQueue';
import useAfterProductionGate from '../hooks/useAfterProductionGate';
import useOrdersQuickAction from '../hooks/useOrdersQuickAction';
import useSidebarCollapsed from '../hooks/useSidebarCollapsed';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import MobileTopBar from './MobileTopBar';
import MobileDrawer from './MobileDrawer';
import SuperAdminShell from './SuperAdminShell';
import MessagingPanel from '../components/MessagingPanel';
import SubscriptionGate from '../components/SubscriptionGate';
import PendingChecklistToast from '../components/PendingChecklistToast';
import SqfImportToast from '../components/SqfImportToast';

const CSS_VARS = {
  '--sidebar-width-expanded': '240px',
  '--sidebar-width-collapsed': '64px',
  '--topbar-height': '56px',
  '--topbar-height-mobile': '52px',
  '--sidebar-bg': '#1b2638',
};

function useIsLoggedIn() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setIsLoggedIn(false); return; }
    try {
      const decoded = jwtDecode(token);
      const nowSec = Math.floor(Date.now() / 1000);
      if (!decoded?.exp || decoded.exp <= nowSec) {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
      } else {
        setIsLoggedIn(true);
      }
    } catch {
      localStorage.removeItem('token');
      setIsLoggedIn(false);
    }
  }, [location]);

  return isLoggedIn;
}

export default function AppLayout() {
  const isLoginPage = useMatch('/');
  const isPrivacyPage = useMatch('/privacy');
  const isTermsPage = useMatch('/terms');

  const isLoggedIn = useIsLoggedIn();
  const { hasModule, companySettings, role, loading: permissionsLoading } = usePermissions();
  const { lang } = useLanguage();
  const { unseenCount } = useOrderNotifications();
  const messagingCtx = useMessaging();
  const totalUnread = messagingCtx?.totalUnread || 0;
  const latestNotification = messagingCtx?.latestNotification;
  const dismissNotification = messagingCtx?.dismissNotification;
  const openChannel = messagingCtx?.openChannel;
  const [chatOpen, setChatOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const canProduction = isLoggedIn && hasModule('ccp.baking.production');
  const canReviewQA = isLoggedIn && hasModule('ccp.baking.qa');

  const { activeRunId, checkingActiveRun, livePath } = useActiveProductionRun(canProduction);
  const { pendingQACount } = useQaReviewQueue(canReviewQA);
  const { visiblePending, dismiss, clearAll } = useAfterProductionGate(canProduction);
  const orders = useOrdersQuickAction({ hasModule, unseenCount });
  const { collapsed, toggle: toggleCollapsed } = useSidebarCollapsed();
  const accessibleItems = useAccessibleNavItems({ isLoggedIn, hasModule, canReviewQA });

  // Auto-dismiss the messaging notification toast after 5s
  useEffect(() => {
    if (!latestNotification) return;
    const t = setTimeout(() => dismissNotification?.(), 5000);
    return () => clearTimeout(t);
  }, [latestNotification, dismissNotification]);

  // Withhold any logo — including the REACT_APP_LOGO_URL fallback — until the tenant's own
  // company settings have actually loaded, so a leftover/default env logo never flashes before
  // the real one arrives.
  const logoUrl = isLoggedIn && !permissionsLoading
    ? companySettings?.navbarLogoUrl || companySettings?.logoUrl || process.env.REACT_APP_LOGO_URL || null
    : null;

  const clock = {
    visible: isLoggedIn && (hasModule('time.tracking.view') || hasModule('time.tracking.manage')) && !!companySettings?.id,
    href: companySettings?.id ? `/time-clock?companyId=${companySettings.id}` : '#',
  };

  // Internal team chat is not available to the confined third-party role (its socket is refused).
  const onOpenChat = role === 'event_manager' ? undefined : () => setChatOpen(true);

  // Bare shell — matches the old Navbar's `return null` for login/legal pages while
  // logged out (no chrome at all, just the page content).
  if ((isLoginPage || isPrivacyPage || isTermsPage) && !isLoggedIn) {
    return <SubscriptionGate><Outlet /></SubscriptionGate>;
  }

  // Superadmin has no company context — hasModule() always returns true for this role,
  // so none of the module-gated sidebar/pinned-actions apply. Separate minimal shell.
  if (role === 'superadmin') {
    return <SuperAdminShell />;
  }

  const badgeValues = { pendingQACount, unseenCount };
  const production = { canProduction, activeRunId, checkingActiveRun, livePath };
  const qaReview = { visible: canReviewQA, badgeCount: pendingQACount };
  const hasOverdue = visiblePending.some(p => p.overdue);

  return (
    <div style={CSS_VARS}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        accessibleItems={accessibleItems}
        badgeValues={badgeValues}
        logoUrl={logoUrl}
      />
      <TopBar
        sidebarCollapsed={collapsed}
        accessibleItems={accessibleItems}
        badgeValues={badgeValues}
        onOpenChat={onOpenChat}
        production={production}
        orders={orders}
        qaReview={qaReview}
        clock={clock}
        totalUnread={totalUnread}
      />
      <MobileTopBar
        onToggleDrawer={() => setDrawerOpen(v => !v)}
        logoUrl={logoUrl}
        clock={clock}
        totalUnread={totalUnread}
        onOpenChat={onOpenChat}
      />
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        accessibleItems={accessibleItems}
        badgeValues={badgeValues}
        production={production}
        orders={orders}
        onOpenChat={onOpenChat}
      />

      <div
        className="app-content"
        style={{
          marginLeft: 'var(--sidebar-width-expanded)',
          paddingTop: 'var(--topbar-height)',
        }}
      >
        <style>{`
          @media (min-width: 992px) {
            .app-content { margin-left: ${collapsed ? 'var(--sidebar-width-collapsed)' : 'var(--sidebar-width-expanded)'} !important; transition: margin-left 0.15s ease; }
          }
          @media (max-width: 991.98px) {
            .app-content { margin-left: 0 !important; padding-top: var(--topbar-height-mobile) !important; }
          }
        `}</style>
        <SubscriptionGate>
          {isLoggedIn && role !== 'event_manager' && <PendingChecklistToast />}
          {isLoggedIn && role !== 'event_manager' && <SqfImportToast />}

          {visiblePending.length > 0 && (
            <div className={`app-no-print alert ${hasOverdue ? 'alert-danger' : 'alert-warning'} d-flex align-items-center gap-2 mb-0 py-2 px-3 rounded-0`}>
              <span style={{ fontSize: 18 }}>{hasOverdue ? '⛔' : '🧹'}</span>
              <div className="flex-fill" style={{ fontSize: 13 }}>
                <strong>{visiblePending.map(p => p.templateName).join(', ')}</strong>
                {' '}{hasOverdue
                  ? (lang === 'es' ? '— vencido, debe completarse.' : '— overdue, must be completed.')
                  : (lang === 'es' ? 'debe completarse al cierre del turno.' : 'must be completed before closing the shift.')}
              </div>
              <a
                href={`/sqf/fill/${visiblePending[0]?.instanceId}`}
                className={`btn ${hasOverdue ? 'btn-danger' : 'btn-warning'} btn-sm text-nowrap`}
                onClick={clearAll}
              >
                {lang === 'es' ? 'Completar Ahora →' : 'Complete Now →'}
              </a>
              {!hasOverdue && (
                <button
                  className="btn btn-sm btn-outline-secondary text-nowrap"
                  style={{ fontSize: 12 }}
                  onClick={dismiss}
                  title={lang === 'es' ? 'Descartar por esta sesión' : 'Dismiss for this session'}
                >
                  ✕
                </button>
              )}
            </div>
          )}

          <Outlet />
        </SubscriptionGate>
      </div>

      {isLoggedIn && hasModule('messaging') && (
        <MessagingPanel open={chatOpen} onClose={() => setChatOpen(false)} />
      )}

      {latestNotification && (
        <div
          onClick={() => { openChannel?.(latestNotification.channelId); setChatOpen(true); dismissNotification?.(); }}
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
            background: 'var(--bs-body-bg, #fff)',
            border: '1px solid var(--bs-border-color, #dee2e6)',
            borderLeft: '4px solid #0d6efd',
            borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            padding: '12px 16px', maxWidth: 320, cursor: 'pointer',
            display: 'flex', alignItems: 'flex-start', gap: 12,
            animation: 'slideUpIn 0.25s ease',
          }}
        >
          <div style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            background: '#0d6efd', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700,
          }}>
            {(latestNotification.authorName || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 2 }}>
              💬 #{latestNotification.channelName}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{latestNotification.authorName}</div>
            <div style={{ fontSize: 13, color: 'var(--bs-body-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {latestNotification.body}
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); dismissNotification?.(); }}
            style={{ border: 'none', background: 'none', color: '#6c757d', cursor: 'pointer', padding: 0, fontSize: 16, lineHeight: 1, flexShrink: 0 }}
          >✕</button>
        </div>
      )}
      <style>{`
        @keyframes slideUpIn {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
